import moment from "moment";
import {
  getTrasladoEstadoColor,
  TRASLADOS_ESTADOS_NOMBRES,
} from "../../../../../constants/trasladosEstados";

const MONTH_LABELS = {
  "01": "Ene",
  "02": "Feb",
  "03": "Mar",
  "04": "Abr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Ago",
  "09": "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dic",
};

export const getDefaultPeriodoFechas = () => ({
  fechaDesde: moment().subtract(90, "days").startOf("day").toDate(),
  fechaHasta: moment().endOf("day").toDate(),
});

const monthLabel = (year, month) => {
  const mm = String(month || "").padStart(2, "0");
  return `${MONTH_LABELS[mm] || mm} ${year || ""}`.trim();
};

const labelFromPeriodo = (periodo) => {
  const match = String(periodo || "").match(/^(\d{4})-(\d{2})/);
  if (match) return monthLabel(match[1], match[2]);
  return "";
};

const getMonthLabelsInRange = (fechaDesde, fechaHasta) => {
  if (!fechaDesde || !fechaHasta) return [];
  const start = moment(fechaDesde).startOf("month");
  const end = moment(fechaHasta).startOf("month");
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return [];

  const labels = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end)) {
    labels.push(monthLabel(cursor.format("YYYY"), cursor.format("MM")));
    cursor.add(1, "month");
  }
  return labels;
};

const estadosCatalogoConExtras = (nombresApi = []) => {
  const seen = new Set();
  const ordered = [];
  nombresApi.forEach((nombre) => {
    if (nombre && !seen.has(nombre)) {
      seen.add(nombre);
      ordered.push(nombre);
    }
  });
  TRASLADOS_ESTADOS_NOMBRES.forEach((nombre) => {
    if (!seen.has(nombre)) {
      seen.add(nombre);
      ordered.push(nombre);
    }
  });
  return ordered;
};

const readApiNumber = (item, camelKey, pascalKey) => {
  const value = item?.[camelKey] ?? item?.[pascalKey];
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const mapSituacionActualToBarMonths = (rows = []) => {
  const byName = {};
  rows.forEach((item) => {
    const nombre = item?.nombre ?? item?.Nombre;
    if (!nombre) return;
    byName[nombre] = readApiNumber(item, "cantidad", "Cantidad") ?? 0;
  });
  const estados = estadosCatalogoConExtras(
    rows.map((item) => item?.nombre ?? item?.Nombre),
  );
  return {
    data: estados.map((nombre) => ({
      date: nombre,
      value: byName[nombre] ?? 0,
    })),
    keys: ["value"],
    estados,
  };
};

export const getSituacionActualResumen = (rows = []) => {
  const cantidadTramos = rows.reduce(
    (sum, item) =>
      sum + (readApiNumber(item, "cantidad", "Cantidad") ?? 0),
    0,
  );
  const cantidadTraslados =
    readApiNumber(
      rows.find(
        (item) =>
          readApiNumber(item, "cantidadTraslados", "CantidadTraslados") !=
          null,
      ),
      "cantidadTraslados",
      "CantidadTraslados",
    ) ?? 0;
  const ratio =
    cantidadTraslados > 0 ? cantidadTramos / cantidadTraslados : null;
  return { cantidadTramos, cantidadTraslados, ratio };
};

export const formatTramosPorTraslado = (ratio) => {
  if (ratio == null || !Number.isFinite(ratio)) return "—";
  return ratio.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const estadoNameFromSerie = (serie) => {
  if (typeof serie === "string") return serie;
  return (
    serie?.id ??
    serie?.indexValue ??
    serie?.data?.date ??
    serie?.date ??
    serie?.art
  );
};

export const getEstadoChartColor = (serie) =>
  getTrasladoEstadoColor(estadoNameFromSerie(serie));

export const getEstadoLegendItems = (nombres = []) =>
  nombres.map((nombre) => ({
    id: nombre,
    label: nombre,
    color: getTrasladoEstadoColor(nombre),
  }));

export const getIntegerTickValues = (...values) => {
  const finite = values.filter((value) => Number.isFinite(value));
  const max = Math.max(1, ...finite.map((value) => Math.ceil(value)));
  return Array.from({ length: max + 1 }, (_, index) => index);
};

export const mapSituacionPeriodoToLine = (
  rows = [],
  fechaDesde,
  fechaHasta,
) => {
  const valuesByEstadoAndMonth = {};
  const nombresApi = [];
  const labelsFromApi = [];

  rows.forEach((mes) => {
    const label = labelFromPeriodo(mes?.periodo ?? mes?.Periodo);
    if (label) labelsFromApi.push(label);
    (mes?.estados ?? mes?.Estados ?? []).forEach((estado) => {
      const nombre = estado?.nombre ?? estado?.Nombre;
      if (!nombre) return;
      nombresApi.push(nombre);
      valuesByEstadoAndMonth[`${nombre}|${label}`] =
        readApiNumber(estado, "cantidad", "Cantidad") ?? 0;
    });
  });

  const rangeLabels = getMonthLabelsInRange(fechaDesde, fechaHasta);
  const monthLabels =
    rangeLabels.length > 0 ? rangeLabels : [...new Set(labelsFromApi)];
  const estados = estadosCatalogoConExtras(nombresApi);

  return estados.map((nombre) => ({
    id: nombre,
    art: nombre,
    data: monthLabels.map((label) => ({
      x: label,
      y: valuesByEstadoAndMonth[`${nombre}|${label}`] ?? 0,
    })),
  }));
};
